// Pure layout math for the sankey-style funnel chart. No React, no DOM:
// stages + transitions in, SVG path strings out, so the geometry is unit
// testable and the component stays presentational.
//
// Visual grammar: every bar is CENTERED on a horizontal midline, so the stream
// narrows symmetrically; each bar splits into a SIGNED-IN segment (solid) over a
// GUEST segment (hatched, surface-gapped); ribbons between consecutive bars carry
// the same split; drop bands leave each bar's lower extent and the two series
// converge into ONE terminal stub per stage; the one column-skipping edge is a
// BYPASS band that sags under the columns it skips. The terminal stage has no
// drop: ending there is the win.

export interface FunnelStageDatum {
  key: string;
  label: string;
  reached: number;
  /** Guest-owned share of `reached`; the signed-in share is the remainder. */
  guestReached: number;
  stalled: number;
  /** Guest-owned share of `stalled` (the drop bands split by series too). */
  guestStalled: number;
}

export interface FunnelTransitionDatum {
  fromKey: string;
  toKey: string;
  count: number;
  guestCount: number;
}

export interface FunnelSegment {
  /** Absolute y of the segment's top edge. */
  y: number;
  h: number;
}

export interface FunnelNodeLayout {
  key: string;
  label: string;
  reached: number;
  /** Left edge of the node bar. */
  x: number;
  y: number;
  /** Full visual height of the bar (both segments + the gap). */
  h: number;
  signed: FunnelSegment;
  /** null when no guests reached this stage. */
  guest: FunnelSegment | null;
}

export interface FunnelBandLayout {
  fromKey: string;
  toKey: string;
  count: number;
  guestCount: number;
  /** The signed-in ribbon, hanging from the shared top line. */
  signedPath: string;
  /** The guest ribbon below it; null when no guests flow on this edge. */
  guestPath: string | null;
  /** Anchor for the hover hit area, at the ribbon's horizontal middle. */
  midX: number;
  midY: number;
}

export interface FunnelDropLayout {
  /** The stage the journey ended at. */
  key: string;
  stalled: number;
  /** The signed-in share's droop; null when every stall is guest-owned. */
  signedPath: string | null;
  /** The guest share's droop (rendered hatched); null when none are guests. */
  guestPath: string | null;
  /** The ONE terminal stub both series converge into: the signed share lands
   *  on [top, top+signedH], the guest share directly beneath it, gap-free. */
  end: { x: number; top: number; signedH: number; guestH: number };
  labelX: number;
  labelY: number;
}

export interface FunnelBypass {
  /** The edge's own endpoints, so a consumer can report WHICH step was
   *  bypassed without re-deriving it. */
  fromKey: string;
  toKey: string;
  /** Direct picks: chose a seller from their own search, no custom offer. */
  count: number;
  guestCount: number;
  signedPath: string | null;
  guestPath: string | null;
  labelX: number;
  labelY: number;
}

export interface FunnelLayout {
  width: number;
  height: number;
  /** The horizontal midline every bar is centered on. */
  axisY: number;
  barWidth: number;
  nodes: FunnelNodeLayout[];
  bands: FunnelBandLayout[];
  drops: FunnelDropLayout[];
  /** The bypass band (the one column-skipping edge this layout draws); null
   *  when there is none. */
  bypass: FunnelBypass | null;
  /** Transitions with a real count that this layout did NOT draw. This layout
   *  draws exactly two things, a band per CONSECUTIVE edge and one bypass, so
   *  this is everything else: a second column-skipping edge, an edge naming a
   *  stage absent from `stages`, and a backward or self edge. Empty in every
   *  funnel we ship. Non-empty means those projects are missing from the picture
   *  with no visible gap, so a consumer must surface it rather than render a
   *  quietly incomplete chart. */
  undrawnTransitions: FunnelTransitionDatum[];
}

// Fixed drawing constants (viewBox units). The SVG scales to its container, so
// these are tuned for a ~55-100% render: the viewBox stays narrow enough that
// label text (set in viewBox units) survives the shrink.
const PAD_X = 12;
/** Headroom above the tallest bar, for its floating label pair. */
const TOP_PAD = 46;
const MAX_H = 200;
/** The chart's midline: bars center on it instead of hanging from a ceiling. */
const AXIS_Y = TOP_PAD + MAX_H / 2;
const BAR_W = 18;
/** Surface gap between the signed and guest segments (dataviz spacer rule). */
const SERIES_GAP = 3;
const DROP_DX = 42;
const DROP_DY = 92;
/** Cap on the combined terminal stub a stage's exit bands converge into. */
const DROP_STUB_MAX = 22;
/** A nonzero count never renders thinner than this, or it becomes invisible. */
const MIN_VISIBLE = 3;

function round2(v: number): number {
  return Math.round(v * 100) / 100;
}

/** Height for a count, floored so tiny-but-real populations stay visible. */
export function scaleHeight(count: number, max: number): number {
  if (count <= 0) return 0;
  const h = (count / max) * MAX_H;
  return round2(Math.max(h, MIN_VISIBLE));
}

/** A ribbon between two vertical edges: bezier top edge t1→t2, bezier bottom
 *  edge b2→b1, both easing through the horizontal midpoint. */
function curvedBand(
  x1: number,
  t1: number,
  b1: number,
  x2: number,
  t2: number,
  b2: number,
): string {
  const mx = round2((x1 + x2) / 2);
  return [
    `M ${x1} ${round2(t1)}`,
    `C ${mx} ${round2(t1)} ${mx} ${round2(t2)} ${x2} ${round2(t2)}`,
    `L ${x2} ${round2(b2)}`,
    `C ${mx} ${round2(b2)} ${mx} ${round2(b1)} ${x1} ${round2(b1)}`,
    "Z",
  ].join(" ");
}

/** A long band sagging BELOW the columns it passes: parallel bezier edges
 *  dipping to `sagY`, connecting two vertical slices. The bypass edge. */
function sagBand(
  x1: number,
  t1: number,
  b1: number,
  x2: number,
  t2: number,
  b2: number,
  sagY: number,
): string {
  const cx1 = round2(x1 + (x2 - x1) * 0.35);
  const cx2 = round2(x1 + (x2 - x1) * 0.65);
  const h = b1 - t1;
  return [
    `M ${x1} ${round2(t1)}`,
    `C ${cx1} ${round2(sagY)} ${cx2} ${round2(sagY)} ${x2} ${round2(t2)}`,
    `L ${x2} ${round2(b2)}`,
    `C ${cx2} ${round2(sagY + h)} ${cx1} ${round2(sagY + h)} ${x1} ${round2(b1)}`,
    "Z",
  ].join(" ");
}

/** A band drooping down-right from a bar slice into an EXPLICIT terminal
 *  slice at `endX`: two bezier edges converging on the stub `[endTop,
 *  endTop+endH]`, so sibling bands aimed at adjacent stubs merge visually. */
function droopTo(
  x: number,
  yA: number,
  thickness: number,
  endX: number,
  endTop: number,
  endH: number,
): string {
  const yB = round2(yA + thickness);
  const cx = round2((x + endX) / 2);
  const endBottom = round2(endTop + endH);
  return [
    `M ${x} ${round2(yA)}`,
    `C ${cx} ${round2(yA)} ${cx} ${round2(endTop)} ${endX} ${round2(endTop)}`,
    `L ${endX} ${endBottom}`,
    `C ${cx} ${endBottom} ${cx} ${yB} ${x} ${yB}`,
    "Z",
  ].join(" ");
}

/** Which edges this layout will DRAW, and which it will not.
 *
 * Split out of `layoutFunnel` so a caller can ask "does this funnel hide
 * anything?" without a full layout (the BI card does), and so the drawable-edge
 * rule has exactly one copy.
 *
 * BYPASS: the first edge with a real count that SKIPS a column, found by shape,
 * never by stage name (recruiter#888). Only one bypass is drawn; stacking
 * several is not built.
 *
 * UNDRAWN: every other edge with a real count, defined by EXCLUSION (not a band,
 * not the bypass), so a backward, self, unknown-endpoint or second skipping edge
 * cannot fall through. "Drawn" means THIS EDGE, not this shape: `bandEdges` keeps
 * the first `a -> b` per column, so a duplicate row is reported as undrawn.
 *
 * An undrawn edge's projects vanish with no visible gap, so this module reports
 * them; consumers set policy.
 */
export function resolveEdges(
  stages: readonly FunnelStageDatum[],
  transitions: readonly FunnelTransitionDatum[],
): {
  bypassEdge: FunnelTransitionDatum | undefined;
  bandEdges: Map<string, FunnelTransitionDatum>;
  undrawnTransitions: FunnelTransitionDatum[];
} {
  const orderOf = new Map(stages.map((s, i) => [s.key, i]));
  const step = (t: FunnelTransitionDatum): number | null => {
    const from = orderOf.get(t.fromKey);
    const to = orderOf.get(t.toKey);
    return from === undefined || to === undefined ? null : to - from;
  };
  const [bypassEdge] = transitions.filter((t) => {
    const d = step(t);
    return d !== null && d > 1 && t.count > 0;
  });
  const bandEdges = new Map<string, FunnelTransitionDatum>();
  for (const t of transitions) {
    const key = `${t.fromKey}->${t.toKey}`;
    if (step(t) === 1 && !bandEdges.has(key)) bandEdges.set(key, t);
  }
  const drawn = new Set<FunnelTransitionDatum>(bandEdges.values());
  const undrawnTransitions = transitions.filter(
    (t) => t.count > 0 && t !== bypassEdge && !drawn.has(t),
  );
  return { bypassEdge, bandEdges, undrawnTransitions };
}

/**
 * Lay the whole funnel out. Pure: same input, same output.
 *
 * - Bar heights scale against the FIRST stage's `reached` (the cohort), so the
 *   leftmost bar is the tallest and every later bar reads as a share of it. Each
 *   bar stacks signed-in over guest with a surface gap, CENTERED on the midline.
 * - Each bar's outflows PARTITION its segments top-to-bottom, per series:
 *   continuing ribbon, then the bypass slice (bypass source only), then the drop
 *   slice, so no two departing bands share an origin pixel. A ribbon lands on the
 *   destination segment's full height, except into the bypass destination, where
 *   it takes the upper slice only.
 * - Drops are TWO-TONE (signed solid, guest hatched), each from its own segment,
 *   landing on ONE shared terminal stub (signed over guest, gap-free).
 * - The column-skipping edge `resolveEdges` picks renders as the BYPASS pair:
 *   per-series bands sagging under the skipped columns into the matching series
 *   slice at the bottom of the destination bar.
 */
export function layoutFunnel(
  stages: readonly FunnelStageDatum[],
  transitions: readonly FunnelTransitionDatum[],
  width = 840,
): FunnelLayout {
  const n = stages.length;
  const height = TOP_PAD + MAX_H + DROP_DY + DROP_STUB_MAX + 28;
  const empty: FunnelLayout = {
    width,
    height,
    axisY: AXIS_Y,
    barWidth: BAR_W,
    nodes: [],
    bands: [],
    drops: [],
    bypass: null,
    // A funnel with no stages draws no edge, so EVERY transition it was handed
    // is undrawn. Returning [] here would be the one place the "never hide an
    // edge silently" guarantee quietly stops holding, and the caller who would
    // be misled is the one reading it from the layout rather than asking
    // `resolveEdges` itself.
    undrawnTransitions: transitions.filter((t) => t.count > 0),
  };
  if (n === 0) return empty;
  const max = Math.max(stages[0]?.reached ?? 0, 1);
  const colW = n > 1 ? (width - 2 * PAD_X - BAR_W) / (n - 1) : 0;

  const nodes: FunnelNodeLayout[] = stages.map((s, i) => {
    const guest = Math.min(Math.max(s.guestReached, 0), s.reached);
    const signedH = scaleHeight(s.reached - guest, max);
    const guestH = scaleHeight(guest, max);
    const gap = signedH > 0 && guestH > 0 ? SERIES_GAP : 0;
    const totalH = round2(signedH + gap + guestH);
    const x = round2(PAD_X + i * colW);
    const barTop = round2(AXIS_Y - totalH / 2);
    return {
      key: s.key,
      label: s.label,
      reached: s.reached,
      x,
      y: barTop,
      h: totalH,
      signed: { y: barTop, h: signedH },
      guest: guestH > 0 ? { y: round2(barTop + signedH + gap), h: guestH } : null,
    };
  });

  const bands: FunnelBandLayout[] = [];
  const drops: FunnelDropLayout[] = [];
  const { bypassEdge, bandEdges, undrawnTransitions } = resolveEdges(stages, transitions);
  const bypassH = bypassEdge ? scaleHeight(bypassEdge.count, max) : 0;

  for (let i = 0; i < n; i += 1) {
    const stage = stages[i];
    const node = nodes[i];
    if (!stage || !node) continue;
    const next = i + 1 < n ? stages[i + 1] : undefined;
    const nextNode = i + 1 < n ? nodes[i + 1] : undefined;
    const edge = next ? bandEdges.get(`${stage.key}->${next.key}`) : undefined;
    const through = edge?.count ?? 0;
    const guestThrough = Math.min(edge?.guestCount ?? 0, through);
    const signedThrough = through - guestThrough;
    const x1 = round2(node.x + BAR_W);

    // Every outflow leaves from its own slice of the bar, top to bottom
    // WITHIN each series' segment: the continuing ribbon first, then the
    // bypass slice (searched only), then the drop slice. No two departing
    // bands ever share an origin pixel.
    const bypassSignedH =
      bypassEdge && stage.key === bypassEdge.fromKey
        ? scaleHeight(bypassEdge.count - bypassEdge.guestCount, max)
        : 0;
    const bypassGuestH =
      bypassEdge && stage.key === bypassEdge.fromKey ? scaleHeight(bypassEdge.guestCount, max) : 0;
    const signedContinueH = next && through > 0 ? scaleHeight(signedThrough, max) : 0;
    const guestContinueH = next && through > 0 ? scaleHeight(guestThrough, max) : 0;
    const srcGuestY = node.guest?.y ?? round2(node.y + node.h + SERIES_GAP);

    if (next && nextNode && through > 0) {
      // The ribbon lands on the destination segment: its full height on plain
      // spine steps (skippers widen honestly), or only its upper slice into a
      // picked bar that also receives the bypass on its lower slice.
      const intoPickedWithBypass = next.key === bypassEdge?.toKey && bypassH > 0;
      const signedH2 = intoPickedWithBypass
        ? Math.min(signedContinueH, nextNode.signed.h)
        : nextNode.signed.h;
      const signedPath = curvedBand(
        x1,
        node.signed.y,
        node.signed.y + signedContinueH,
        nextNode.x,
        nextNode.signed.y,
        nextNode.signed.y + signedH2,
      );
      const guestRightY = nextNode.guest?.y ?? round2(nextNode.y + nextNode.h + SERIES_GAP);
      const guestH2Full = nextNode.guest?.h ?? 0;
      const guestH2 = intoPickedWithBypass ? Math.min(guestContinueH, guestH2Full) : guestH2Full;
      const guestPath =
        guestContinueH > 0 || guestH2 > 0
          ? curvedBand(
              x1,
              srcGuestY,
              srcGuestY + guestContinueH,
              nextNode.x,
              guestRightY,
              guestRightY + guestH2,
            )
          : null;
      bands.push({
        fromKey: stage.key,
        toKey: next.key,
        count: through,
        guestCount: guestThrough,
        signedPath,
        guestPath,
        midX: round2((x1 + nextNode.x) / 2),
        midY: round2(
          (node.signed.y + signedContinueH / 2 + nextNode.signed.y + nextNode.signed.h / 2) / 2,
        ),
      });
    }

    const isTerminal = i === n - 1;
    if (isTerminal) continue;

    if (stage.stalled > 0) {
      const guestStalled = Math.min(Math.max(stage.guestStalled, 0), stage.stalled);
      const signedStalled = stage.stalled - guestStalled;
      const signedDropH = scaleHeight(signedStalled, max);
      const guestDropH = scaleHeight(guestStalled, max);
      // Each series' drop leaves right below that series' continuing ribbon
      // (and bypass slice), inside its own segment...
      const signedDropY = round2(node.signed.y + signedContinueH + bypassSignedH);
      const guestDropY = round2(srcGuestY + guestContinueH + bypassGuestH);
      // ...but both converge into ONE terminal stub, split pro-rata (each
      // present series floored so it stays legible), so the exit reads as a
      // single chute leaving the funnel instead of two parallel stubs.
      const endX = round2(x1 + DROP_DX);
      const totalDropH = signedDropH + guestDropH;
      const endStub = round2(Math.min(totalDropH, DROP_STUB_MAX));
      let guestEndH = totalDropH > 0 ? (guestDropH / totalDropH) * endStub : 0;
      if (signedDropH > 0 && guestDropH > 0) {
        guestEndH = Math.min(Math.max(guestEndH, 2), endStub - 2);
      }
      guestEndH = round2(guestEndH);
      const signedEndH = round2(endStub - guestEndH);
      const endTop = round2((guestDropH > 0 ? guestDropY : signedDropY) + DROP_DY);
      drops.push({
        key: stage.key,
        stalled: stage.stalled,
        signedPath:
          signedDropH > 0 ? droopTo(x1, signedDropY, signedDropH, endX, endTop, signedEndH) : null,
        guestPath:
          guestDropH > 0
            ? droopTo(x1, guestDropY, guestDropH, endX, round2(endTop + signedEndH), guestEndH)
            : null,
        end: { x: endX, top: endTop, signedH: signedEndH, guestH: guestEndH },
        labelX: round2(endX + 6),
        labelY: round2(endTop + endStub / 2),
      });
    }
  }

  // The bypass pair: each series' slice leaves the searched bar right below
  // its continuing ribbon and sags under the offers column into the matching
  // series slice at the BOTTOM of the picked bar.
  let bypass: FunnelBypass | null = null;
  // Both ends come from the edge itself, not from hardcoded stage names, so a
  // funnel with a different vocabulary still draws its own bypass.
  const searchedIdx = bypassEdge ? stages.findIndex((s) => s.key === bypassEdge.fromKey) : -1;
  const searchedNode = nodes[searchedIdx];
  const pickedNode = bypassEdge ? nodes.find((nd) => nd.key === bypassEdge.toKey) : undefined;
  if (bypassEdge && bypassH > 0 && searchedNode && pickedNode && searchedIdx >= 0) {
    const sStage = stages[searchedIdx];
    const sNext = stages[searchedIdx + 1];
    const sEdge = sNext && sStage ? bandEdges.get(`${sStage.key}->${sNext.key}`) : undefined;
    const sThrough = sEdge?.count ?? 0;
    const sGuestThrough = Math.min(sEdge?.guestCount ?? 0, sThrough);
    const sSignedContinueH = sThrough > 0 ? scaleHeight(sThrough - sGuestThrough, max) : 0;
    const sGuestContinueH = sThrough > 0 ? scaleHeight(sGuestThrough, max) : 0;

    const x1 = round2(searchedNode.x + BAR_W);
    const bSignedH = scaleHeight(bypassEdge.count - bypassEdge.guestCount, max);
    const bGuestH = scaleHeight(bypassEdge.guestCount, max);
    const srcSignedY = round2(searchedNode.signed.y + sSignedContinueH);
    const srcGuestSegY = searchedNode.guest?.y ?? round2(searchedNode.y + searchedNode.h);
    const srcGuestY = round2(srcGuestSegY + sGuestContinueH);

    const destSignedBottom = round2(pickedNode.signed.y + pickedNode.signed.h);
    const destGuestBottom = pickedNode.guest
      ? round2(pickedNode.guest.y + pickedNode.guest.h)
      : round2(pickedNode.y + pickedNode.h);
    const pBottom = round2(pickedNode.y + Math.max(pickedNode.h, MIN_VISIBLE));
    const sBottom = round2(searchedNode.y + Math.max(searchedNode.h, MIN_VISIBLE));
    const sagY = round2(Math.max(sBottom, pBottom) + 34);
    bypass = {
      fromKey: bypassEdge.fromKey,
      toKey: bypassEdge.toKey,
      count: bypassEdge.count,
      guestCount: bypassEdge.guestCount,
      signedPath:
        bSignedH > 0
          ? sagBand(
              x1,
              srcSignedY,
              srcSignedY + bSignedH,
              pickedNode.x,
              destSignedBottom - bSignedH,
              destSignedBottom,
              sagY,
            )
          : null,
      guestPath:
        bGuestH > 0
          ? sagBand(
              x1,
              srcGuestY,
              srcGuestY + bGuestH,
              pickedNode.x,
              destGuestBottom - bGuestH,
              destGuestBottom,
              round2(sagY + bSignedH + 3),
            )
          : null,
      labelX: round2((x1 + pickedNode.x) / 2),
      labelY: round2(sagY + bypassH * 0.75 + 14),
    };
  }

  return {
    width,
    height,
    axisY: AXIS_Y,
    barWidth: BAR_W,
    nodes,
    bands,
    drops,
    bypass,
    undrawnTransitions,
  };
}

/** Percentage share, safe on a zero denominator. */
export function sharePct(part: number, whole: number): number | null {
  if (whole <= 0) return null;
  return (part / whole) * 100;
}

/** "94%", "7.5%", "<1%" — coarse above 10, one decimal below, honest on tiny. */
export function formatPct(value: number | null): string {
  if (value === null) return "-";
  if (value > 0 && value < 1) return "<1%";
  if (value < 10) return `${Math.round(value * 10) / 10}%`;
  return `${Math.round(value)}%`;
}

/** "3.5 days", "5 h", "same day" — days for real waits, hours under a day. */
export function formatDays(days: number | null | undefined): string {
  if (days === null || days === undefined) return "-";
  if (days < 0.04) return "same day";
  if (days < 1) return `${Math.max(1, Math.round(days * 24))} h`;
  return `${Math.round(days * 10) / 10} days`;
}

/** Whole-dollar committed budget: "$1,250". */
export function formatUsd(value: number | null | undefined): string {
  if (value === null || value === undefined) return "-";
  return `$${Math.round(value).toLocaleString("en-US")}`;
}

/** Compact money for chart labels: "$830", "$12.4k", "$1.25M". */
export function formatUsdShort(value: number | null | undefined): string {
  if (value === null || value === undefined) return "-";
  const v = Math.round(value);
  if (Math.abs(v) >= 1_000_000) return `$${Math.round((v / 1_000_000) * 100) / 100}M`;
  if (Math.abs(v) >= 10_000) return `$${Math.round(v / 100) / 10}k`;
  return `$${v.toLocaleString("en-US")}`;
}
